import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { t } from 'ember-intl'
import formatValue from 'teacher-ui-client/helpers/format-value'
import { concat } from '@ember/helper'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import formatMathseedsPrimeLessonCode from 'teacher-ui-client/helpers/format-mathseeds-prime-lesson-code'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { ProgressOverviewReportData } from 'teacher-ui-client/reporting/ui-scope/mathseeds-prime/dashboard-student/index/route'
import { get } from '@ember/helper'
import { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import type FeaturesService from 'teacher-ui-client/services/features'

interface Args {
  isLoading?: boolean
  overviewData: ProgressOverviewReportData['overview']
}

interface Signature {
  Args: Args
  Element: HTMLTableElement
}

export class DashboardStudentProgressOverview extends Component<Signature> {
  @service features!: FeaturesService

  get threads() {
    if (this.features.isEnabled('tui_ms_prime_measurement_thread')) {
      return [
        MathseedsPrimeThread.Number,
        MathseedsPrimeThread.Fractions,
        MathseedsPrimeThread.Operations,
        MathseedsPrimeThread.Geometry,
        MathseedsPrimeThread.Measurement,
      ]
    }
    return [
      MathseedsPrimeThread.Number,
      MathseedsPrimeThread.Fractions,
      MathseedsPrimeThread.Operations,
      MathseedsPrimeThread.Geometry,
    ]
  }

  <template>
    <table
      exportable={{t "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.exportTitle"}}
      class="relative"
      ...attributes
    >
      <thead>
        <tr class="bg-ms-green-50 divide-x divide-white">
          <th class="p-3 font-semibold">
            {{t "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.headers.primeLessons"}}
          </th>
          <th class="p-3 text-center font-semibold">
            {{t
              "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.headers.initialLessonCompleted"
            }}
          </th>
          <th class="p-3 text-center font-semibold">
            {{t
              "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.headers.highestLessonCompleted"
            }}
          </th>
          <th class="p-3 text-center font-semibold">
            {{t "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.headers.averageScore"}}
          </th>
          <th class="p-3 text-center font-semibold">
            {{t
              "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.headers.lessonsCompleted"
            }}
          </th>
          <th class="p-3 text-center font-semibold">
            {{t "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.headers.unitsCompleted"}}
          </th>
        </tr>
      </thead>
      {{#if @isLoading}}
        <tbody>
          <tr>
            <td class="absolute left-1/2 -translate-x-1/2 transform p-3 text-center">
              <FidgetSpinnerWaveComponent data-test-table-loading @small={{true}} @centered={{true}} />
            </td>
          </tr>
        </tbody>
      {{/if}}
      <tbody class="divide-ms-green-100 divide-y {{if @isLoading 'opacity-50'}}">
        {{#each this.threads as |thread|}}
          {{#let (get @overviewData.threads thread) as |threadRow|}}
            <tr data-test-thread-row={{thread}}>
              <td class="p-3" data-test-thread-name>{{t (concat "mathseedsPrimeThreads." thread ".name")}}</td>
              <td class="p-3 text-center" data-test-initial-lesson>
                {{formatValue
                  (formatMathseedsPrimeLessonCode
                    thread threadRow.initial_lesson_completed.unit_id threadRow.initial_lesson_completed.lesson_id
                  )
                }}
              </td>
              <td class="p-3 text-center" data-test-highest-lesson>
                {{formatValue
                  (formatMathseedsPrimeLessonCode
                    thread threadRow.highest_lesson_completed.unit_id threadRow.highest_lesson_completed.lesson_id
                  )
                }}
              </td>
              <td class="p-3 text-center" data-test-average-score>
                {{formatValue threadRow.average_score append="%"}}
              </td>
              <td class="p-3 text-center" data-test-lessons-completed>{{formatValue threadRow.lessons_completed}}</td>
              <td class="p-3 text-center" data-test-units-completed>{{formatValue threadRow.units_completed}}</td>
            </tr>
          {{/let}}
        {{/each}}
      </tbody>
      <tfoot class="divide-y divide-white {{if @isLoading 'opacity-50'}}">
        <tr class="bg-ms-green-50 divide-x divide-white">
          <td class="p-3">
            {{t "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.footer.totalCompleted"}}
          </td>
          <td></td>
          <td></td>
          <td></td>
          <td class="p-3 text-center font-semibold" data-test-total-lessons-completed>
            {{@overviewData.aggregates.total_lessons_completed}}
          </td>
          <td class="p-3 text-center font-semibold" data-test-total-units-completed>
            {{@overviewData.aggregates.total_units_completed}}
          </td>
        </tr>
        <tr class="bg-ms-green-100 divide-x divide-white">
          <td class="p-3">
            {{t
              "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.footer.totalLessonsMastered"
            }}
            <span class="print:hidden">
              <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 cursor-pointer" />
              <Tooltip
                @text={{t
                  "components.reporting.tables.mathseedsPrime.dashboardStudent.progressOverview.footer.totalLessonsMasteredTooltip"
                }}
              />
            </span>
          </td>
          <td></td>
          <td></td>
          <td></td>
          <td class="p-3 text-center font-semibold" data-test-total-lessons-mastered>
            {{@overviewData.aggregates.total_lessons_mastered}}
          </td>
          <td></td>
        </tr>
      </tfoot>
    </table>
  </template>
}

export default DashboardStudentProgressOverview
